// A single row in the feedback list (docs/06-ux-rules.md "List" / "Row
// anatomy"; 11-chassis-plan.md §2 PR 04; prototype/v69 `ibRender()`,
// index.html:2276-2281). Full-width `Button kind="minimal"` (repo
// convention: uikit `Button` for every clickable, including borderless
// list-item-style controls) so keyboard/click both land on a real button —
// `useRovingListNav`'s `getItemProps(index)` supplies `tabIndex`/
// `onKeyDown`/`onClick`/`ref`; `aria-selected` is supplied separately here
// (not from `getItemProps`, whose own `aria-selected` tracks the roving
// FOCUS stop, not the URL-driven `item=` selection — see the container's
// header comment for why those two can differ, e.g. on initial load before
// any keyboard interaction, per "no auto-select on load").
import { Button } from '@thanx/uikit/button'
import type { FeedbackInboxItem } from 'api/feedbackInbox.types'
import imgWordmark from 'assets/images/recovery-ai/img_wordmark.svg'
import FeedbackRatingIcon from 'components/FeedbackRatingIcon'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { formatShortDate } from 'utilities/dateFormatters'
import {
  RESOLVE_CHECK_POP_MS,
  RESOLVE_COLLAPSE_DELAY_MS,
  RESOLVE_COLLAPSE_MS,
  RESOLVE_EXIT_FADE_MS,
  RESOLVE_EXIT_WASH_OPACITY,
  RESOLVE_FLASH_WASH_OPACITY,
  RESOLVE_STATUS_IN_MS,
  RESOLVE_SWIPE_MS,
  RESOLVE_WASH_MS,
} from '../constants'
import type { RovingListNavItemProps } from '../useRovingListNav'
import { inboxRowStatus, type StatusTone } from './inboxRowStatus'
import type { ResolveAnimationPhase } from './useResolveAnimation'

const t = buildTranslate('feedback_inbox.list')
const tStatus = buildTranslate('feedback_inbox.status_line')

const AVATAR_SIZE_PX = 30
const AVATAR_ICON_SIZE_PX = 15
const CHECK_POP_SIZE_PX = 34
const CHECK_POP_ICON_SIZE_PX = 19
// Row text sizes (v83 `.ib-it-name` / `.ib-it-snip` / `.ib-it-time` and
// `.ib-it-status`).
const NAME_FONT_SIZE = '14px'
const PREVIEW_FONT_SIZE = '13px'
const META_FONT_SIZE = '11.5px'
// Wordmark inside the RecoveryAI badge — with the pill's 3px vertical
// padding, this renders at v83's 18px `.rai-badge-svg` height.
const RECOVERY_AI_WORDMARK_HEIGHT_PX = 12

// Prototype's `.ib-ava.up`/`.down` (index.html:462-467,883) — a LIGHT tint
// circle (not a solid fill) with the thumb glyph itself carrying the
// saturated color. `background` values are near-exact matches already in
// `constants/palette.ts`: prototype `#fdecec` ≈ `palette.cayenne05`
// (`#fdecea`), prototype `#e6f6ec` ≈ `palette.nephritis10` (`#E9F6EF`).
// Row-local: `FeedbackRatingIcon` (shared with FeedbackTable /
// RecentActivitiesTable) renders the bare glyph in a table cell — it has no
// notion of this tinted circle, so the tint stays here rather than being
// pushed into the shared component.
const AVATAR_BACKGROUND: Record<'up' | 'down', string> = {
  up: palette.nephritis10,
  down: palette.cayenne05,
}

// v83 `.ib-it-status.need` is #8a6d1a (= eggYolk70); every other status
// line is grey70.
const STATUS_TONE_COLOR: Record<StatusTone, string> = {
  warning: palette.eggYolk70,
  grey: palette.grey70,
}

// Extracted (rather than left inline on the `<Button>` below) so the
// padding regression this fixes is directly testable, mirroring
// `segmentButtonOverrides` in InboxFilterBar/index.tsx and its own
// `— padding (found live)` test.
//
// See InboxFilterBar/index.tsx's header comment: this REPLACES
// @thanx/uikit's own `kind="minimal"` style generator (uikit spreads
// the consumer's overrides after its own — a shallow merge at the
// slot level), and since uikit never forwards `kind` to baseui,
// anything left undeclared falls through to baseui's DEFAULT
// `$kind = primary`. `:hover` is declared for exactly that reason:
// baseui's primary hover is a near-black `buttonPrimaryHover`
// (#002E4C), and while the row's own opaque background (on the inner
// `<span>` below) currently papers over it completely, relying on a
// child to mask a near-black wash is one layout change away from
// showing at the row's edges — the same omission was visible on
// LookbackBackHeader, which has no such child.
export const rowButtonOverrides = {
  BaseButton: {
    style: () =>
      ({
        width: '100%',
        height: 'auto',
        // Longhand, not shorthand `padding: 0` — baseui's own BaseButton
        // sizes itself via longhand paddingTop/Bottom/Left/Right keyed
        // off $size (button/styled-components.js), and the object this
        // style function returns gets merged with that base object
        // key-by-key, not replaced wholesale. A shorthand `padding` key
        // doesn't unset baseui's own longhand keys — it just becomes a
        // second, competing declaration in the same atomic-rendered
        // style object (the exact "mixing shorthand and longhand..." case
        // InboxFilterBar/index.tsx's header comment documents for this
        // same BaseButton/override interaction). Confirmed live: with
        // only `padding: 0`, baseui's own 14px top/bottom (`scale550`)
        // leaked through on every row, inflating the whole list's height
        // well past the row's own intended 15px padding.
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
        border: 'none',
        ':hover': {
          backgroundColor: 'transparent',
        },
        // Outside JSX's contextual typing against `ButtonOverrides`, TS widens
        // the `0`s above to plain `number` instead of the CSS-length-allowed
        // literal `0` — `as const` keeps them literal (mirrors the string
        // literals `segmentButtonOverrides` marks `as const` in
        // InboxFilterBar/index.tsx, same class of widening).
      } as const),
    // `role="option"` (review finding #8): the listbox parent
    // (`InboxList`'s `getListProps()`) may only contain `role="option"`
    // children — a bare `<button>` is invalid ARIA there. Confirmed at
    // runtime that uikit's `Button` forwards a bare `role` prop all the
    // way to the native element (`baseui`'s `Button` spreads unknown
    // props onto its styled `<button>`), but uikit's own `Button.d.ts`
    // doesn't list `role` among the props it re-exports, so TypeScript
    // rejects `role` passed directly on `<Button>`. `overrides.*.props`
    // is baseui's own (loosely-typed, `any`) supported extension point
    // for exactly this — not a type-escape hatch of our own.
    props: { role: 'option' },
  },
}

export interface InboxListRowProps {
  item: FeedbackInboxItem
  selected: boolean
  animationPhase: ResolveAnimationPhase
  navProps: RovingListNavItemProps
}

const InboxListRow = ({
  item,
  selected,
  animationPhase,
  navProps,
}: InboxListRowProps) => {
  const [css] = useStyletron()
  const status = inboxRowStatus(item)
  // v83 `.ib-item.unread` — a "Needs response" row bolds its name to 800.
  const isUnread = status.tone === 'warning'
  const name = item.customer.name ?? t('unknown_guest')

  // Resolve micro-interaction overlays (docs/06-ux-rules.md "Resolve
  // animation"). `flash`/`checking` layer a green wash under the row's
  // normal content (matches prototype's `::after` overlay); `exiting`
  // additionally swipes the whole row left and collapses it — see
  // `useResolveAnimation`'s header comment for the phase timeline these
  // correspond to.
  // Padding/divider/hover/selected match the prototype's `.ib-item` row
  // almost exactly (index.html:462-466) — selected uses a light GREY fill
  // (`palette.grey10`), not a brand-blue tint; hover is a lighter grey
  // still (`palette.grey05`), and a selected row's hover stays at the
  // selected shade (prototype: `.ib-item.sel, .ib-item.sel:hover` share one
  // rule).
  // Every duration below comes from `../constants` (docs/06-ux-rules.md
  // "Resolve animation" / prototype/v69's `resolveRowExit()`) — none is
  // hardcoded here. `max-height`/`padding` carry the prototype's 340ms
  // delay (`.42s ease .34s`): the swipe reads first, then the row collapses,
  // rather than collapsing while it's still visibly sliding.
  const rowStyle = css({
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '11px',
    padding: '15px 30px 15px 18px',
    textAlign: 'left',
    borderBottom: `1px solid ${palette.grey10}`,
    backgroundColor: selected ? palette.grey10 : palette.white,
    transition: [
      'background-color 0.12s',
      `transform ${RESOLVE_SWIPE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      `opacity ${RESOLVE_EXIT_FADE_MS}ms ease`,
      `max-height ${RESOLVE_COLLAPSE_MS}ms ease ${RESOLVE_COLLAPSE_DELAY_MS}ms`,
      `padding ${RESOLVE_COLLAPSE_MS}ms ease ${RESOLVE_COLLAPSE_DELAY_MS}ms`,
    ].join(', '),
    ...(animationPhase === 'exiting' && {
      transform: 'translateX(-115%)',
      opacity: 0,
      maxHeight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      overflow: 'hidden',
    }),
    ':hover': {
      backgroundColor: selected ? palette.grey10 : palette.grey05,
    },
  })

  // v83: the in-place flash fades the wash OUT from 0.18 (`resolveFade`);
  // a leaving row holds a static 0.15 under the check-pop and swipe.
  const isLeaving =
    animationPhase === 'checking' || animationPhase === 'exiting'
  const washStyle = css({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundColor: palette.nephritis,
    opacity: isLeaving ? RESOLVE_EXIT_WASH_OPACITY : 0,
    ...(animationPhase === 'flash' && {
      animationName: {
        '0%': { opacity: RESOLVE_FLASH_WASH_OPACITY },
        '100%': { opacity: 0 },
      } as unknown as string,
      animationDuration: `${RESOLVE_WASH_MS}ms`,
      animationTimingFunction: 'ease-out',
      animationFillMode: 'forwards',
    }),
  })

  // v83 `resolveStatusIn`: the flipped-to-resolved label fades up in place.
  const statusStyle = css({
    color: STATUS_TONE_COLOR[status.tone],
    fontSize: META_FONT_SIZE,
    fontWeight: 400,
    marginTop: '3px',
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px',
    ...(animationPhase === 'flash' && {
      animationName: {
        '0%': { opacity: 0, transform: 'translateY(4px)' },
        '100%': { opacity: 1, transform: 'none' },
      } as unknown as string,
      animationDuration: `${RESOLVE_STATUS_IN_MS}ms`,
      animationTimingFunction: 'ease-out',
      animationFillMode: 'both',
    }),
  })

  // The resolve check-pop (docs/06-ux-rules.md "Resolve animation";
  // prototype's `resolveCheckPop` keyframe, index.html:1359-1375) — a
  // circle that pops in over RESOLVE_CHECK_POP_MS, then holds its final
  // scale/opacity (`animationFillMode: 'forwards'`) through the rest of the
  // `checking` hold and the following `exiting` swipe, since the same DOM
  // node stays mounted across both phases (see useResolveAnimation's phase
  // timeline) — it only unmounts once the row itself is removed.
  // Nephritis (`palette.nephritis`, #27AE60) at 40% opacity — the prototype's
  // exact `rgba(39,174,96,.4)` shadow; a shadow's alpha channel isn't
  // expressible via the solid `palette` token, same precedent as
  // InboxFilterBar's SEGMENT_ACTIVE_SHADOW.
  const checkPopStyle = css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: `${CHECK_POP_SIZE_PX}px`,
    height: `${CHECK_POP_SIZE_PX}px`,
    borderRadius: '50%',
    backgroundColor: palette.nephritis,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    pointerEvents: 'none',
    boxShadow: '0 4px 14px rgba(39, 174, 96, 0.4)',
    animationName: {
      '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.4)' },
      '45%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.12)' },
      '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
    } as unknown as string,
    animationDuration: `${RESOLVE_CHECK_POP_MS}ms`,
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
  })

  return (
    <Button
      {...navProps}
      aria-selected={selected}
      kind="minimal"
      overrides={rowButtonOverrides}
    >
      <span className={rowStyle}>
        <span
          className={washStyle}
          aria-hidden="true"
          data-testid="resolve-wash"
        />
        {isLeaving && (
          <span className={checkPopStyle} aria-hidden="true">
            <svg
              width={CHECK_POP_ICON_SIZE_PX}
              height={CHECK_POP_ICON_SIZE_PX}
              viewBox="0 0 24 24"
              fill="none"
              stroke={palette.white}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        )}
        {/* aria-hidden: the status line below already conveys the rating
            (Needs response / Escalated / Resolved / Positive) — this glyph
            is decorative, not a second source of that information. */}
        <span
          aria-hidden="true"
          className={css({
            position: 'relative',
            flexShrink: 0,
            width: `${AVATAR_SIZE_PX}px`,
            height: `${AVATAR_SIZE_PX}px`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: AVATAR_BACKGROUND[item.rating],
          })}
        >
          <FeedbackRatingIcon rating={item.rating} size={AVATAR_ICON_SIZE_PX} />
        </span>
        <span
          className={css({
            position: 'relative',
            minWidth: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          })}
        >
          <span
            className={css({
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '8px',
            })}
          >
            <span
              className={css({
                fontSize: NAME_FONT_SIZE,
                fontWeight: isUnread ? 800 : 700,
                color: palette.grey90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {name}
            </span>
            <span
              className={css({
                fontSize: META_FONT_SIZE,
                // Explicit: the row sits inside a uikit Button (weight 500).
                fontWeight: 400,
                color: palette.grey70,
                flexShrink: 0,
              })}
            >
              {formatShortDate(item.date)}
            </span>
          </span>
          {item.preview && (
            <p
              className={css({
                margin: 0,
                fontSize: PREVIEW_FONT_SIZE,
                fontWeight: 400,
                color: palette.grey70,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {t('preview_quoted', { comment: item.preview })}
            </p>
          )}
          <span className={statusStyle}>
            <span>{status.label}</span>
            {status.recoveryAiBadge && (
              <span
                className={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: palette.spearmint10,
                  borderRadius: '4px',
                  padding: '3px 7px',
                })}
              >
                <img
                  src={imgWordmark}
                  alt={tStatus('recovery_ai_alt')}
                  className={css({
                    height: `${RECOVERY_AI_WORDMARK_HEIGHT_PX}px`,
                    width: 'auto',
                    display: 'block',
                  })}
                />
              </span>
            )}
          </span>
        </span>
      </span>
    </Button>
  )
}

export default InboxListRow
